import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Button, Page } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { Head } from '@zg-rentals/next/app';

import accessUtils from '../../../srcLegacy/shared/utils/accessUtils';
import accessUtilsNext from '../../utils/accessUtils';

import { useGetPropertiesList } from '../../hooks/queries/properties/zrm';
import {
  closeBanner,
  initializeFiltersFromRouter,
  isFromMarketingApplicationsRoute,
  isFromMarketingLeasesRoute,
  isFromMarketingPaymentsRoute,
  isRedirectToPayments,
  removeQueryParams,
  renderInitialDisplayModal,
  transformFiltersToApiParams,
  updateRouterWithFilters,
} from '../../utils/propertiesPageUtils';

import QueryConstants from '../../../srcLegacy/shared/constants/QueryConstants';
import ErrorRMW from '../../../srcLegacy/shared/model/ErrorRMW';
import PropertiesConstants from '../../constants/PropertiesConstants';
import { USER_ONBOARDING_DISMISSED_KEY } from '../../constants/UserOnboardingConstants';
import { EXTERNAL_ROUTES } from '../../constants/urls';

import {
  emitAddFeedClickEvent,
  emitAddPropertyClickEvent,
  emitOpenEvent,
} from '../../analytics/events/addPropertyModal';
import { useAuth } from '../../components/AuthProvider';
import AddFeedListingsModal from '../../components/modals/AddFeedListingsModal';
import AddPropertyModal from '../../components/modals/AddPropertyModal';
import PaymentsAggregateViewModal from '../../components/modals/PaymentsAggregateViewModal';
import UserOnboarding from '../../components/modals/UserOnboarding';
import EmailVerification from '../../components/properties/EmailVerification';
import NotificationBanner from '../../components/properties/NotificationBanner';
import Notifications from '../../components/properties/Notifications';

import {
  emitViewPropertiesPageEvent,
  EVENT_IDS as PROPERTIES_PAGE_EVENT_IDS,
} from '../../analytics/events/propertiesPage';
import AuthCheckWrapper from '../AuthCheckWrapper';
import { Filters } from './Filters';
import { PageHeading } from './PageHeading';
import { PropertiesPageSkeleton } from './PropertiesPageSkeleton';
import { PropertyTable } from './PropertyTable';

import type { SetStateAction } from 'react';
import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';
import type { FilterState } from '../../utils/propertiesPageUtils';

const defaultFilters: FilterState = {
  searchText: PropertiesConstants.PROPERTIES_SEARCHTEXT_DEFAULT,
  subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
};

export type BannerToCloseArg =
  | 'displayPaymentsNotification'
  | 'displayApplicationsNotification'
  | 'displayLeasesNotification'
  | 'displayListingClaimCompleteBanner';

const PropertiesPage = () => {
  const router = useRouter();
  const { user } = useAuth();

  const isCheckInitialDisplayModal = useRef(true);

  const [filters, setFilters] = useState<FilterState>(() => initializeFiltersFromRouter(router.query, defaultFilters));
  const [isValidatingFilters, setIsValidatingFilters] = useState(true);

  const [showAddFeedListingsModal, setShowAddFeedListingsModal] = useState(false);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [showPaymentsNotification, setShowPaymentsNotification] = useState(false);
  const [showPaymentsAggregateViewModal, setShowPaymentsAggregateViewModal] = useState(false);
  const [showApplicationsNotification, setShowApplicationsNotification] = useState(false);
  const [showLeasesNotification, setShowLeasesNotification] = useState(false);
  const [showListingClaimCompleteBanner, setShowListingClaimCompleteBanner] = useState(false);
  const [showUserOnboardingExistingUser, setShowingUserOnboardingExisitngUser] = useState(false);

  const canAccessUserOnboardingNewUser = accessUtilsNext.canAccessUserOnboardingNewUser({
    user,
  });
  const canAccessUserOnboardingExistingUser = accessUtilsNext.canAccessUserOnboardingExistingUser({
    user,
  });

  const [isVewingUserOnboardingNewUser, setIsViewingUserOnboardingNewUser] = useState(canAccessUserOnboardingNewUser);

  const {
    data: propertiesData,
    error: propertiesError,
    isError,
    isPending,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetPropertiesList(transformFiltersToApiParams(filters), {
    enabled: Boolean(user?.loggedIn && user?.restrictions?.isAllowed),
  });

  const { listingGroups = [], listingCount } = propertiesData ?? { listingCount: undefined, pager: undefined };

  const {
    total: numTotalListings = 0,
    freeClaimedListingCount: numClaimedListings = 0,
    archived: numArchivedListings = 0,
    freeBonusListingCount: numBonusListings = 0,
    atRisk: numAtRiskListings = 0,
  } = listingCount ?? {};

  const isArchivedView = filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED;

  useEffect(() => {
    emitViewPropertiesPageEvent({ user });
  }, []);

  // Scroll to top and clear search when switching views
  useEffect(() => {
    window.scrollTo(0, 0);

    if (isArchivedView && filters.searchText) {
      setFilters({ ...filters, searchText: '' });
    }
  }, [isArchivedView]);

  // Handle marketing routes and notifications
  useEffect(() => {
    const query = router.query;
    const isFromMarketingPayments = isFromMarketingPaymentsRoute(query);
    const isFromMarketingLeases = isFromMarketingLeasesRoute(query);
    const isFromMarketingApplications = isFromMarketingApplicationsRoute(query);

    if (user?.restrictions?.isAllowed) {
      if (isFromMarketingPayments) {
        setShowPaymentsNotification(true);
      } else if (isFromMarketingLeases) {
        setShowLeasesNotification(true);
      } else if (isFromMarketingApplications && user.applicationsEnabled) {
        setShowApplicationsNotification(true);
      }
    }

    // Check for listing claim complete banner
    if (query.itc === 'listing_claim_complete') {
      setShowListingClaimCompleteBanner(true);
    }
  }, []);

  // Validate filters once data has loaded
  useEffect(() => {
    if (!isPending) {
      const validatedFilters = validateFiltersAccess(initializeFiltersFromRouter(router.query, filters));
      setFilters(validatedFilters);

      if (validatedFilters.subNavFilterType !== filters.subNavFilterType) {
        updateRouterWithFilters(router, validatedFilters);
      }

      setIsValidatingFilters(false);
    }
  }, [router.asPath, isPending]);

  // Check for initial display modal
  useEffect(() => {
    if (isCheckInitialDisplayModal.current && !isPending) {
      isCheckInitialDisplayModal.current = false;
      checkInitialDisplayModal();
    }
  }, [isPending, numTotalListings]);

  // Re-check for modals when specific query params change
  useEffect(() => {
    const postingPath = router.query[QueryConstants.POSTING_PATH];
    const addAFeed = router.query[QueryConstants.ADD_A_FEED];

    if (postingPath || addAFeed) {
      checkInitialDisplayModal();
    }
  }, [router.query[QueryConstants.POSTING_PATH], router.query[QueryConstants.ADD_A_FEED]]);

  // Show payments aggregate view modal if conditions met
  useEffect(() => {
    if (
      user?.features?.canAccessMainPayments &&
      !user?.hasSeenPaymentsAggregatedViewModal &&
      (user?.paymentsAcceptedCount ?? 0) >= 2
    ) {
      setShowPaymentsAggregateViewModal(true);
    }
  }, [user?.features?.canAccessMainPayments]);

  // Handle errors
  useEffect(() => {
    if (isError && propertiesError) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorRMWModel = ErrorRMW((propertiesError as any).error);

      logError({
        error: errorRMWModel,
        context: 'fetchGroupableProperties',
      });
    }
  }, [isError, propertiesError]);

  const validateFiltersAccess = (filters: FilterState): FilterState => {
    const { subNavFilterType } = filters;

    if (
      (subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED && numClaimedListings === 0) ||
      (subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK && numAtRiskListings === 0) ||
      (subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS &&
        numBonusListings === 0) ||
      (subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED && numArchivedListings === 0) ||
      !Object.values(PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE).includes(subNavFilterType)
    ) {
      return {
        ...filters,
        subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
      };
    }
    return filters;
  };

  const checkInitialDisplayModal = () => {
    // Create a minimal properties object with a listings array
    // renderInitialDisplayModal only needs listings[0].listingAlias for single property redirects
    const propertiesForModal = {
      listings: listingGroups.map((group) => ({ listingAlias: group.listingAlias })),
      listingCount: listingCount ?? {},
    };

    renderInitialDisplayModal({
      router,
      totalListingsWithoutFilters: numTotalListings,
      showAddPropertyModal,
      setShowAddPropertyModal: handleAddPropertyClick,
      canAccessUserOnboardingNewUser: Boolean(canAccessUserOnboardingNewUser && isVewingUserOnboardingNewUser),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      properties: propertiesForModal as any,
      setShowAddFeedListingsModal: () => {
        if (user?.restrictions?.isAllowed && accessUtils.canAccessAddFeedListingsModal({ user })) {
          setShowAddFeedListingsModal(true);
        }
      },
      setShowingUserOnboardingExisitngUser,
      canAccessUserOnboardingExistingUser,
    });
  };

  const updateFilters = (newFilters: SetStateAction<FilterState>) => {
    setFilters((current) => {
      const next = typeof newFilters === 'function' ? newFilters(current) : newFilters;
      updateRouterWithFilters(router, next);
      return next;
    });
  };

  const handleAddPropertyClick = ({ shouldSendAnalytics = false }: { shouldSendAnalytics?: boolean } = {}) => {
    const shouldRedirectToPayments = isRedirectToPayments(router.query);

    if (user?.features?.canAddListing && user?.restrictions?.isAllowed) {
      setShowAddPropertyModal(true);
    }

    if (shouldSendAnalytics) {
      emitOpenEvent({
        user: user as ZrmClickstreamUser,
        triggerObject: shouldRedirectToPayments ? 'start_collecting_rent_button' : undefined,
      });

      emitAddPropertyClickEvent({ user: user as ZrmClickstreamUser });
    }
  };

  const handleAddFeedListingsClick = () => {
    setShowAddFeedListingsModal(true);
    emitAddFeedClickEvent({ user: user as ZrmClickstreamUser });
  };

  const onBannerClose = (bannerToClose: BannerToCloseArg) => {
    closeBanner(
      bannerToClose,
      setShowPaymentsNotification,
      setShowApplicationsNotification,
      setShowLeasesNotification,
      setShowListingClaimCompleteBanner,
    );
  };

  const canAccessFeatureNotifications = user ? accessUtils.canAccessFeatureNotifications({ user }) : false;

  const isContentLoading = isPending || isFetching || isFetchingNextPage;
  const hasAnyListings = numTotalListings > 0 || numArchivedListings > 0;

  let onboardingConfig = null;
  if (canAccessUserOnboardingNewUser && isVewingUserOnboardingNewUser) {
    onboardingConfig = {
      onClose: () => setIsViewingUserOnboardingNewUser(false),
      size: 'fullScreen' as const,
      isNewUser: true,
    };
  } else if (showUserOnboardingExistingUser) {
    onboardingConfig = {
      onClose: () => {
        if (typeof window !== 'undefined') {
          sessionStorage.setItem(USER_ONBOARDING_DISMISSED_KEY, 'true');
        }
        setShowingUserOnboardingExisitngUser(false);
      },
      size: { base: 'fullScreen', md: 'sm' } as const,
      isNewUser: (user?.summary?.totalListingCount ?? 0) === 0,
    };
  }

  return (
    <AuthCheckWrapper loading={<PropertiesPageSkeleton />} redirecting={<PropertiesPageSkeleton />}>
      <Page.Root
        data-testid="PropertiesPage"
        data-eventid={PROPERTIES_PAGE_EVENT_IDS.VIEW_PROPERTIES_PAGE}
        data-eventdescription="View properties page"
      >
        <Head applicationName="zillow-rental-manager-web" buildNumber={process.env.NEXT_PUBLIC_BUILD_NUMBER}>
          <title>Properties | Zillow Rental Manager</title>
        </Head>
        {canAccessFeatureNotifications && <NotificationBanner />}
        <PageHeading
          handleAddPropertyClick={handleAddPropertyClick}
          handleAddFeedListingsClick={handleAddFeedListingsClick}
        />
        <EmailVerification />
        <Notifications
          displayPaymentsNotification={showPaymentsNotification}
          displayApplicationsNotification={showApplicationsNotification}
          displayLeasesNotification={showLeasesNotification}
          displayListingClaimCompleteBanner={showListingClaimCompleteBanner}
          handleBannerCloseClick={onBannerClose}
        />
        <Page.Content css={{ padding: { base: '0 {spacing.600}', xl: '0 {spacing.1200}' } }}>
          <Box>
            {(hasAnyListings || isContentLoading) && <Filters filters={filters} setFilters={updateFilters} />}
            <PropertyTable filters={filters} setFilters={updateFilters} isSkeletonView={isValidatingFilters} />
          </Box>

          {hasNextPage && (
            <Box css={{ display: 'flex', justifyContent: 'center', marginTop: '600' }}>
              <Button tone="brand" emphasis="filled" onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
                Load more properties
              </Button>
            </Box>
          )}
        </Page.Content>
        {/* Modals */}
        {showAddFeedListingsModal && (
          <AddFeedListingsModal
            onClose={() => {
              setShowAddFeedListingsModal(false);
              removeQueryParams(router, [QueryConstants.ADD_A_FEED]);
            }}
          />
        )}
        {onboardingConfig && <UserOnboarding {...onboardingConfig} />}
        {showAddPropertyModal && (
          <AddPropertyModal
            isOpen={showAddPropertyModal}
            onClose={() => {
              setShowAddPropertyModal(false);
              removeQueryParams(router, [QueryConstants.POSTING_PATH, QueryConstants.REDIRECT_TO]);
            }}
          />
        )}
        {showPaymentsAggregateViewModal && (
          <PaymentsAggregateViewModal
            onClose={() => setShowPaymentsAggregateViewModal(false)}
            paymentsAggregatePageUrl={EXTERNAL_ROUTES.PAYMENTS_DASHBOARD}
          />
        )}
      </Page.Root>
    </AuthCheckWrapper>
  );
};

export default PropertiesPage;
